import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";
import { closeSidebar } from "../app/sidebarSlice";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import RightPanel from "../components/RightPanel";
import InfoText from "../components/InfoText";
import { DESIGN_TOKENS } from "../designSystem/tokens";

const Main = ({ children, showRightPanel = true, showInfoText = true }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const { isOpen, collapsed } = useSelector((state) => state.sidebar);
  const sidebarWidth = collapsed
    ? DESIGN_TOKENS.layout.sidebarCollapsed
    : DESIGN_TOKENS.layout.sidebarExpanded;

  // Close mobile drawer on route change
  useEffect(() => {
    dispatch(closeSidebar());
  }, [location.pathname, dispatch]);

  // Lock body scroll while mobile drawer is open
  useEffect(() => {
    if (isDesktop || !isOpen) {
      document.body.style.overflow = "";
      return undefined;
    }
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isDesktop, isOpen]);

  // Leaving mobile → always close drawer
  useEffect(() => {
    if (isDesktop) dispatch(closeSidebar());
  }, [isDesktop, dispatch]);

  return (
    <div className="ds-app flex h-[100dvh] overflow-hidden bg-[var(--bg-app)] w-full max-w-[100vw]">
      {/* Desktop rail only — never mount on mobile (avoids flex squash) */}
      {isDesktop && (
        <aside
          className="ds-sidebar flex flex-col flex-shrink-0 h-full z-20 overflow-hidden"
          style={{
            width: sidebarWidth,
            transition: "width 0.22s ease",
          }}
        >
          <Sidebar collapsed={collapsed} />
        </aside>
      )}

      {/* Mobile drawer — fixed overlay, outside main flex sizing */}
      {!isDesktop && (
        <div
          className={`fixed inset-0 z-[1400] ${
            isOpen ? "pointer-events-auto" : "pointer-events-none"
          }`}
          aria-hidden={!isOpen}
        >
          <div
            className={`absolute inset-0 bg-black/45 backdrop-blur-[1px] transition-opacity duration-200 ${
              isOpen ? "opacity-100" : "opacity-0"
            }`}
            onClick={() => dispatch(closeSidebar())}
          />
          <aside
            className="absolute top-0 left-0 h-full flex flex-col bg-[var(--surface-color)] shadow-[var(--shadow-lg)] border-r border-[var(--border-color)] transition-transform duration-200 ease-out"
            style={{
              width: Math.min(DESIGN_TOKENS.layout.sidebarExpanded, 300),
              maxWidth: "86vw",
              transform: isOpen ? "translateX(0)" : "translateX(-105%)",
            }}
          >
            <Sidebar mobile />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col h-full overflow-hidden min-w-0 w-full">
        <div className="flex-shrink-0 z-30 bg-[var(--surface-color)]">
          <Navbar />
        </div>

        <div className="flex flex-1 overflow-hidden min-h-0 min-w-0">
          <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-y-auto overflow-x-hidden no-scrollbar ds-page">
            {children}
            {showInfoText && (
              <div className="mt-4 ds-section">
                <InfoText />
              </div>
            )}
          </div>

          {showRightPanel && isDesktop && (
            <div
              className="flex w-64 flex-shrink-0 overflow-hidden
              h-[calc(100dvh-4.5rem)] mt-4 mr-4 mb-4 ds-card !p-0"
            >
              <RightPanel />
            </div>
          )}
        </div>
      </div>

      {/* Mobile services FAB lives inside RightPanel; mount panel for FAB only */}
      {showRightPanel && !isDesktop && <RightPanel />}
    </div>
  );
};

export default Main;
